import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { forkJoin } from 'rxjs';
import { AppState } from './../../../../App.state';
import { NovoDashboardService } from 'src/app/base/services/novo-dashboard.service';
import { DialogService } from 'src/app/global/services/dialog.service';
import { QueryOptions } from 'src/app/global/domain/query.options';
import {
  InadimplenciaCliente,
  InadimplenciaContrato,
  InadimplenciaEvolucao,
  InadimplenciaFaixaAtraso,
  InadimplenciaKpis,
  InadimplenciaReincidente,
  SituacaoInadimplencia
} from 'src/app/base/domain/dto/historico-inadiplencia.dto';

Chart.register(...registerables);

type AbaDetalhe = 'contratos' | 'faixas' | 'clientes' | 'reincidentes';

/**
 * As duas situações reproduzem as consultas dos relatórios de clientes inadimplentes do SGF:
 * REGULAR = contrato com saldo em atraso ainda vigente, IRREGULAR = contrato já vencido com saldo.
 */
const SITUACOES: { valor: SituacaoInadimplencia; descricao: string }[] = [
  { valor: 'TODAS', descricao: 'Todas as situações' },
  { valor: 'REGULAR', descricao: 'Em andamento' },
  { valor: 'IRREGULAR', descricao: 'Em atraso' }
];

/** Verde → vermelho conforme a chance de recuperação cai. */
const CORES_FAIXA = ['#4caf50', '#8bc34a', '#ffc107', '#ff9800', '#f4511e', '#d32f2f'];

@Component({
  selector: 'app-dashboard-historico-inadiplencia',
  templateUrl: './dashboard-historico-inadiplencia.component.html',
  styleUrls: ['./dashboard-historico-inadiplencia.component.css']
})
export class DashboardHistoricoInadiplenciaComponent implements OnInit, OnDestroy {

  @ViewChild('graficoHistoricoInadiplencia', { static: true })
  canvas!: ElementRef<HTMLCanvasElement>;

  estado = '';
  situacao: SituacaoInadimplencia = 'TODAS';

  estadosDisponiveis: string[] = [];
  readonly situacoes = SITUACOES;

  kpis?: InadimplenciaKpis;
  contratos: InadimplenciaContrato[] = [];
  evolucao: InadimplenciaEvolucao[] = [];
  faixas: InadimplenciaFaixaAtraso[] = [];
  rankingClientes: InadimplenciaCliente[] = [];
  reincidentes: InadimplenciaReincidente[] = [];

  mostrarDetalhes = false;
  abaDetalhe: AbaDetalhe = 'contratos';
  limiteRanking = 20;
  limiteContratos = 1000;

  // Paginacao da tabela de contratos, no mesmo padrao do dashboard de parcelas.
  contratosPaginados: InadimplenciaContrato[] = [];
  paginaAtual = 1;
  tamanhoPagina = 15;
  totalPaginas = 1;

  private chart?: Chart;

  constructor(
    private service: NovoDashboardService,
    private appState: AppState,
    public dialogService: DialogService
  ) { }

  ngOnInit(): void {
    this.carregarEstados();
    this.carregarDashboard();
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private carregarEstados(): void {
    this.service.findEstadosInadimplencia().subscribe({
      next: estados => this.estadosDisponiveis = estados || [],
      error: () => this.estadosDisponiveis = []
    });
  }

  public carregarDashboard(): void {
    this.appState.isLoading = true;

    forkJoin({
      kpis: this.service.findInadimplenciaKpis(this.montarQuery()),
      contratos: this.service.findInadimplenciaContratos(this.montarQuery({ limite: this.limiteContratos })),
      evolucao: this.service.findInadimplenciaEvolucao(this.montarQuery()),
      faixas: this.service.findInadimplenciaFaixasAtraso(this.montarQuery()),
      clientes: this.service.findInadimplenciaRankingClientes(this.montarQuery({ limite: this.limiteRanking })),
      reincidentes: this.service.findInadimplenciaReincidentes(this.montarQueryReincidentes())
    }).subscribe({
      next: resultado => {
        this.kpis = resultado.kpis;
        this.contratos = resultado.contratos || [];
        this.paginaAtual = 1;
        this.atualizarPagina();
        this.evolucao = resultado.evolucao || [];
        this.faixas = resultado.faixas || [];
        this.rankingClientes = resultado.clientes || [];
        this.reincidentes = resultado.reincidentes || [];
        this.montarGrafico();
        this.appState.isLoading = false;
      },
      error: () => {
        this.dialogService.feedbackError('Erro ao carregar o dashboard de inadimplência.');
        this.appState.isLoading = false;
      }
    });
  }

  private montarQuery(extras: { [key: string]: string | number } = {}): QueryOptions {
    return new QueryOptions({
      params: { estado: this.estado, situacao: this.situacao, ...extras }
    });
  }

  /** Reincidentes vêm de outra view, que não carrega a classificação regular/irregular. */
  private montarQueryReincidentes(): QueryOptions {
    return new QueryOptions({
      params: { estado: this.estado, limite: this.limiteRanking }
    });
  }

  private montarGrafico(): void {
    if (this.chart) {
      this.chart.destroy();
    }

    const labels = this.evolucao.map(item => this.rotuloPeriodo(item.periodo));
    const valores = this.evolucao.map(item => item.valorInadimplente ?? 0);

    this.chart = new Chart(this.canvas.nativeElement, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: 'Valor inadimplente (R$)',
          data: valores,
          borderColor: '#d32f2f',
          backgroundColor: 'rgba(211, 47, 47, 0.12)',
          borderWidth: 2,
          tension: 0.3,
          fill: true,
          pointRadius: 2,
          pointHoverRadius: 5
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: true, position: 'bottom' },
          tooltip: {
            callbacks: {
              afterBody: (itens: any[]) => {
                const registro = this.evolucao[itens[0].dataIndex];
                if (!registro) {
                  return '';
                }
                return [
                  `Contratado: ${this.formatarMoeda(registro.valorContratado)}`,
                  `Contratos: ${registro.contratos}`,
                  `Clientes: ${registro.clientes}`
                ];
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { callback: (valor: any) => this.formatarMoedaCurta(valor) }
          }
        }
      }
    });
  }

  // --- Tabela de contratos ---

  private atualizarPagina(): void {
    this.totalPaginas = Math.ceil(this.contratos.length / this.tamanhoPagina) || 1;
    const inicio = (this.paginaAtual - 1) * this.tamanhoPagina;
    this.contratosPaginados = this.contratos.slice(inicio, inicio + this.tamanhoPagina);
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.atualizarPagina();
    }
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.atualizarPagina();
    }
  }

  // Totais sobre todos os contratos filtrados, nao so os da pagina — como no rodape do relatorio.

  get totalValorBruto(): number {
    return this.somar(item => item.valorBruto);
  }

  get totalValorPago(): number {
    return this.somar(item => item.valorPago);
  }

  get totalSaldoDevedor(): number {
    return this.somar(item => item.saldoDevedor);
  }

  get totalDiferenca(): number {
    return this.somar(item => item.diferenca);
  }

  private somar(campo: (item: InadimplenciaContrato) => number): number {
    return this.contratos.reduce((soma, item) => soma + (campo(item) || 0), 0);
  }

  // --- Drill-down ---

  alternarDetalhes(): void {
    this.mostrarDetalhes = !this.mostrarDetalhes;
  }

  selecionarAba(aba: AbaDetalhe): void {
    this.abaDetalhe = aba;
  }

  /** Participação da faixa no total inadimplente, usada na barra da tabela. */
  participacaoFaixa(faixa: InadimplenciaFaixaAtraso): number {
    const total = this.faixas.reduce((soma, item) => soma + (item.valorInadimplente || 0), 0);
    return total > 0 ? ((faixa.valorInadimplente || 0) / total) * 100 : 0;
  }

  corFaixa(faixa: InadimplenciaFaixaAtraso): string {
    return CORES_FAIXA[(faixa.ordem || 1) - 1] || CORES_FAIXA[CORES_FAIXA.length - 1];
  }

  get descricaoSituacao(): string {
    const encontrada = SITUACOES.find(s => s.valor === this.situacao);
    return encontrada ? encontrada.descricao : 'Todas as situações';
  }

  // --- Helpers ---

  private formatarMoeda(valor: number): string {
    return (valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  /** Eixo do gráfico: R$ 1,2 mi / R$ 350 mil, para não estourar a largura. */
  private formatarMoedaCurta(valor: number): string {
    const numero = valor || 0;
    if (Math.abs(numero) >= 1000000) {
      return `R$ ${(numero / 1000000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
    }
    if (Math.abs(numero) >= 1000) {
      return `R$ ${(numero / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} mil`;
    }
    return `R$ ${numero}`;
  }

  /** O backend devolve sempre o primeiro dia do mês. */
  private rotuloPeriodo(periodo: string): string {
    if (!periodo) {
      return '';
    }
    const [ano, mes] = periodo.split('-');
    return `${mes}/${ano}`;
  }
}
